// What the profile cannot be saved without, and what each field has to look
// like once it holds anything. Written the way the brand form's rules are, and
// for the same reason: each field's rule sits next to the normaliser that feeds
// it, so the shape the form accepts and the shape it stores cannot drift apart.
//
// The list of required fields is short on purpose. This screen is a person
// describing themselves, not a record another system depends on: what the app
// actually needs is a name to head the profile with, and a number to reach
// them on. Everything else is checked once there is something in it and left
// alone while there is not.

import {
  digitsOf,
  mobileError,
  pincodeError,
} from "../../lib/fieldFormat";
import type { DietitianProfile } from "./types/settings.types";

export const REQUIRED_FIELDS = [
  "firstName",
  "lastName",
  "gender",
  "phone",
] as const;

/**
 * Optional fields that are still checked once something has been typed into
 * them — leaving the WhatsApp number blank is fine, half a number in it is
 * not. Age is here rather than above because a profile without one is a
 * profile that simply does not say, and 3 in it is a typo either way.
 */
export const OPTIONAL_VALIDATED_FIELDS = [
  "age",
  "whatsapp",
  "pincode",
  "experienceYears",
  "experienceMonths",
] as const;

export type RequiredField = (typeof REQUIRED_FIELDS)[number];
export type OptionalValidatedField = (typeof OPTIONAL_VALIDATED_FIELDS)[number];
export type ValidatedField = RequiredField | OptionalValidatedField;

export const VALIDATED_FIELDS = [
  ...REQUIRED_FIELDS,
  ...OPTIONAL_VALIDATED_FIELDS,
] as const;

export type ProfileErrors = Partial<Record<ValidatedField, string>>;

/** Names used in "<field> is required", so the message reads like the label. */
const LABELS: Record<ValidatedField, string> = {
  firstName: "First name",
  lastName: "Last name",
  gender: "Gender",
  phone: "Phone number",
  age: "Age",
  whatsapp: "WhatsApp number",
  pincode: "Pincode",
  experienceYears: "Work experience",
  experienceMonths: "Work experience",
};

/** Nobody practising is younger than this, and nobody is older. */
export const MIN_AGE = 18;
export const MAX_AGE = 100;

/** A working life is long, but it is not longer than this. */
export const MAX_EXPERIENCE_YEARS = 70;

/** A person's name: letters, plus the joiners that appear inside one. */
const NAME_PATTERN = /^[A-Za-z][A-Za-z\s.'-]*[A-Za-z.]$/;

const isRequired = (field: ValidatedField): field is RequiredField =>
  (REQUIRED_FIELDS as readonly string[]).includes(field);

/** Empty string means the field is fine. */
export const fieldError = (field: ValidatedField, raw: string): string => {
  const value = raw.trim();

  // An empty optional field has nothing to be wrong about.
  if (!value) return isRequired(field) ? `${LABELS[field]} is required` : "";

  switch (field) {
    case "firstName":
    case "lastName":
      // Digits get their own message: a name field holding one is nearly
      // always a value pasted into the wrong row, and "invalid name" would
      // leave the reader looking for a spelling mistake that is not there.
      if (/\d/.test(value)) return `${LABELS[field]} cannot contain digits`;
      return NAME_PATTERN.test(value)
        ? ""
        : `Enter a valid ${LABELS[field].toLowerCase()}`;
    case "age": {
      const age = Number(value);
      return age >= MIN_AGE && age <= MAX_AGE
        ? ""
        : `Age must be between ${MIN_AGE} and ${MAX_AGE}`;
    }
    case "phone":
    case "whatsapp":
      return mobileError(LABELS[field], value);
    case "pincode":
      return pincodeError(value);
    case "experienceYears":
      return Number(value) <= MAX_EXPERIENCE_YEARS
        ? ""
        : `Work experience cannot be more than ${MAX_EXPERIENCE_YEARS} years`;
    // Anything a year or more belongs in the field beside this one, which is
    // what the two figures are for.
    case "experienceMonths":
      return Number(value) <= 11 ? "" : "Months must be between 0 and 11";
    default:
      return "";
  }
};

/** Only failing fields appear, so an empty object means the form is valid. */
export const validateProfile = (profile: DietitianProfile): ProfileErrors => {
  const errors: ProfileErrors = {};

  VALIDATED_FIELDS.forEach((field) => {
    const message = fieldError(field, profile[field]);
    if (message) errors[field] = message;
  });

  return errors;
};

/* ── Input normalisers ─────────────────────────────────────────────────────
   The profile's own; phone, pincode and name casing are shared with the brand
   and client forms and live in `lib/fieldFormat`. Applied on every keystroke,
   so a field can only ever hold a shape the rules above recognise.        */

/** Digits only, stopped at the three an age can need. */
export const normaliseAgeInput = (raw: string): string =>
  digitsOf(raw).slice(0, 3);

/** Digits only, stopped at two — both experience figures are small numbers. */
export const normaliseExperienceInput = (raw: string): string =>
  digitsOf(raw).slice(0, 2);

// How these same fields are read back out — the heading, the experience line,
// the address on one line — lives in `profileFormat`. Rules here, wording
// there.
